import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  AttachMoney,
  School,
  Restaurant,
  Print,
  Book,
  ShoppingCart,
  Receipt,
  Discount
} from '@mui/icons-material';
import realDataAccountingService from '../../services/realDataAccountingService';
import comprehensiveJournalService from '../../services/comprehensiveJournalService';

function ComprehensiveAccountingDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comprehensiveData, setComprehensiveData] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Redux data
  const reduxData = {
    students: useSelector((state) => state.students) || [],
    teachers: useSelector((state) => state.teachers) || [],
    expenses: useSelector((state) => state.expenses) || [],
    sales: useSelector((state) => state.sales) || [],
    assets: useSelector((state) => state.assets) || [],
    class: useSelector((state) => state.class) || [],
    schooldetails: useSelector((state) => state.schooldetails) || []
  };

  const schoolID = reduxData.schooldetails?.[0]?._id || 'default';
  const startDate = new Date(new Date().getFullYear(), 0, 1); // Start of year
  const endDate = new Date(); // Current date

  const loadComprehensiveData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if we have basic data
      if (!reduxData.students || reduxData.students.length === 0) {
        setError('No student data available. Please ensure data is loaded.');
        setLoading(false);
        return;
      }

      const data = await realDataAccountingService.generateComprehensiveFinancialReports(
        schoolID, startDate, endDate, reduxData
      );
      setComprehensiveData(data);
    } catch (err) {
      console.error('Error loading comprehensive data:', err);
      setError(`Failed to load comprehensive financial data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [schoolID, startDate, endDate, reduxData]);

  useEffect(() => {
    // Debug: Log available data
    console.log('ComprehensiveAccountingDashboard - Redux Data:', {
      students: reduxData.students?.length || 0,
      teachers: reduxData.teachers?.length || 0,
      expenses: reduxData.expenses?.length || 0,
      sales: reduxData.sales?.length || 0,
      assets: reduxData.assets?.length || 0,
      class: reduxData.class?.length || 0,
      schooldetails: reduxData.schooldetails?.length || 0,
      schoolID
    });
    
    // Only load data if we have some basic data
    if (reduxData.students?.length > 0 || reduxData.teachers?.length > 0 || reduxData.expenses?.length > 0) {
      loadComprehensiveData();
    } else {
      console.log('No data available yet, will retry in 2 seconds...');
      // Retry after 2 seconds in case data is still loading
      const timer = setTimeout(() => {
        loadComprehensiveData();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [loadComprehensiveData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const formatCurrency = (amount) => {
    // Get currency from school details (derive from country if currency not set)
    const currency = reduxData.schooldetails?.[0]?.currency 
      ? String(reduxData.schooldetails[0].currency).toUpperCase()
      : (reduxData.schooldetails?.[0]?.country === 'LR' ? 'USD' : 'GHS');
    const locale = reduxData.schooldetails?.[0]?.locale 
      ? reduxData.schooldetails[0].locale 
      : (reduxData.schooldetails?.[0]?.country === 'LR' ? 'en-US' : 'en-GH');
    
    return new Intl.NumberFormat(locale || 'en-GH', {
      style: 'currency',
      currency: currency || 'GHS',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getRevenueIcon = (type) => {
    const icons = {
      schoolFees: <School />,
      admissionFees: <Receipt />,
      printingFees: <Print />,
      booksRevenue: <Book />,
      uniformRevenue: <ShoppingCart />,
      canteenRevenue: <Restaurant />,
      dailyCharges: <AttachMoney />,
      weeklyCharges: <AttachMoney />,
      monthlyCharges: <AttachMoney />,
      termlyCharges: <AttachMoney />,
      otherRevenue: <Receipt />,
      salesRevenue: <ShoppingCart />
    };
    return icons[type] || <AttachMoney />;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Loading comprehensive financial data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!comprehensiveData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No comprehensive financial data available. Please ensure all data sources are properly configured.
        </Alert>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Data Status:</Typography>
          <Typography variant="body2">
            Students: {reduxData.students?.length || 0} | 
            Teachers: {reduxData.teachers?.length || 0} | 
            Expenses: {reduxData.expenses?.length || 0} | 
            Sales: {reduxData.sales?.length || 0} | 
            Assets: {reduxData.assets?.length || 0} | 
            Classes: {reduxData.class?.length || 0}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            School ID: {schoolID}
          </Typography>
        </Alert>
        <Button variant="contained" onClick={loadComprehensiveData} disabled={loading}>
          {loading ? 'Loading...' : 'Retry Loading Data'}
        </Button>
      </Box>
    );
  }

  const { incomeStatement, balanceSheet, classWiseAnalysis, chargesBreakdown } = comprehensiveData;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Comprehensive Financial Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Complete financial overview covering all aspects of school operations
      </Typography>

      <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Revenue Breakdown" />
        <Tab label="Class Analysis" />
        <Tab label="Charges Detail" />
        <Tab label="Balance Sheet" />
      </Tabs>

      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Revenue</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {formatCurrency(incomeStatement.revenue.totalRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All revenue streams combined
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingDown color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Expenses</Typography>
                </Box>
                <Typography variant="h4" color="error">
                  {formatCurrency(incomeStatement.expenses.totalExpenses)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Operating costs
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <AccountBalance color={incomeStatement.netIncome >= 0 ? "success" : "error"} sx={{ mr: 1 }} />
                  <Typography variant="h6">Net Income</Typography>
                </Box>
                <Typography variant="h4" color={incomeStatement.netIncome >= 0 ? "success.main" : "error.main"}>
                  {formatCurrency(incomeStatement.netIncome)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Profit/Loss
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Discount color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Discounts Given</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {formatCurrency(incomeStatement.adjustments.discountsGiven)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total discounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Active Classes
                    </Typography>
                    <Typography variant="h6">
                      {classWiseAnalysis.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Students
                    </Typography>
                    <Typography variant="h6">
                      {classWiseAnalysis.reduce((sum, classInfo) => sum + classInfo.studentCount, 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Avg Revenue/Student
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(
                        classWiseAnalysis.reduce((sum, classInfo) => sum + classInfo.averageRevenuePerStudent, 0) / 
                        Math.max(classWiseAnalysis.length, 1)
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Revenue Sources
                    </Typography>
                    <Typography variant="h6">
                      {Object.keys(incomeStatement.revenue).filter(key => key !== 'totalRevenue' && incomeStatement.revenue[key] > 0).length}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Revenue Breakdown
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Revenue Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                    <TableCell align="center">Icon</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(incomeStatement.revenue)
                    .filter(([key, value]) => key !== 'totalRevenue' && value > 0)
                    .map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getRevenueIcon(key)}
                            <Typography sx={{ ml: 1 }}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6">
                            {formatCurrency(value)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${((value / incomeStatement.revenue.totalRevenue) * 100).toFixed(1)}%`}
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {getRevenueIcon(key)}
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Total Revenue
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(incomeStatement.revenue.totalRevenue)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label="100%" color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <TrendingUp />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Class-wise Financial Analysis
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell align="right">Students</TableCell>
                    <TableCell align="right">Total Revenue</TableCell>
                    <TableCell align="right">Avg/Student</TableCell>
                    <TableCell align="right">School Fees</TableCell>
                    <TableCell align="right">Other Charges</TableCell>
                    <TableCell align="right">Discounts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classWiseAnalysis.map((classInfo) => (
                    <TableRow key={classInfo.className}>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {classInfo.className}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {classInfo.studentCount}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="primary">
                          {formatCurrency(classInfo.totalRevenue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(classInfo.averageRevenuePerStudent)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(classInfo.breakdown.schoolFees)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          classInfo.breakdown.admissionFees +
                          classInfo.breakdown.printingFees +
                          classInfo.breakdown.booksCharges +
                          classInfo.breakdown.uniformCharges +
                          classInfo.breakdown.canteenFees +
                          classInfo.breakdown.otherCharges
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="warning.main">
                          {formatCurrency(classInfo.breakdown.discounts)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detailed Charges Breakdown
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(chargesBreakdown).map(([chargeType, data]) => (
                <Grid item xs={12} md={6} lg={4} key={chargeType}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {chargeType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </Typography>
                      <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                        {formatCurrency(data.total)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {data.transactions.length} transactions
                      </Typography>
                      {data.transactions.length > 0 && (
                        <Box sx={{ mt: 1, maxHeight: 150, overflow: 'auto' }}>
                          {data.transactions.slice(0, 5).map((transaction, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption">
                                {transaction.studentName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(transaction.amount)}
                              </Typography>
                            </Box>
                          ))}
                          {data.transactions.length > 5 && (
                            <Typography variant="caption" color="text.secondary">
                              ... and {data.transactions.length - 5} more
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assets
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Assets
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(balanceSheet.assets.currentAssets.cash)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cash and Cash Equivalents
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Accounts Receivable
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(balanceSheet.assets.currentAssets.accountsReceivable)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Inventory
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(balanceSheet.assets.currentAssets.inventory)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" color="primary">
                  Total Assets: {formatCurrency(
                    balanceSheet.assets.currentAssets.cash +
                    balanceSheet.assets.currentAssets.accountsReceivable +
                    balanceSheet.assets.currentAssets.inventory +
                    Object.values(balanceSheet.assets.fixedAssets).reduce((sum, val) => sum + val, 0)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Liabilities & Equity
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Liabilities
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      balanceSheet.liabilities.currentLiabilities.accountsPayable +
                      balanceSheet.liabilities.currentLiabilities.accruedExpenses
                    )}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Capital
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(balanceSheet.equity.capital)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Retained Earnings
                  </Typography>
                  <Typography variant="h6" color={balanceSheet.equity.retainedEarnings >= 0 ? "success.main" : "error.main"}>
                    {formatCurrency(balanceSheet.equity.retainedEarnings)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" color="primary">
                  Total Equity: {formatCurrency(
                    balanceSheet.equity.capital + balanceSheet.equity.retainedEarnings
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={loadComprehensiveData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={() => window.print()}
        >
          Print Report
        </Button>
      </Box>
    </Box>
  );
}

export default ComprehensiveAccountingDashboard;
